import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import PageHeader from "@/components/layout/PageHeader";
import Seo from "@/components/Seo";
import { getBlogPostSeo } from "@/data/seoData";
import { getPostBySlug, getRelatedPosts } from "@/data/blogData";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("bg-BG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const ContentBlock = ({ block }) => {
  if (block.h2) {
    return (
      <h2 className="text-2xl md:text-3xl font-bold text-[#2E4A91] mt-10 mb-4">
        {block.h2}
      </h2>
    );
  }
  if (block.list) {
    return (
      <ul className="space-y-3 my-4">
        {block.list.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-slate-600">
            <CheckCircle className="w-5 h-5 text-[#2ED573] mt-1 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="text-lg text-slate-600 leading-relaxed mb-5">{block.p}</p>
  );
};

const NotFound = () => (
  <Layout>
    <Seo path="/blog" title="Статията не е намерена" noindex />
    <PageHeader subtitle="Блог" title="Статията не е намерена" />
    <section className="section-padding bg-white text-center">
      <div className="max-w-2xl mx-auto px-4">
        <p className="text-lg text-slate-600 mb-8">
          За съжаление, тази статия не съществува или е премахната.
        </p>
        <Link to="/blog">
          <Button className="btn-primary">
            <ArrowLeft className="mr-2 w-4 h-4" />
            Към всички статии
          </Button>
        </Link>
      </div>
    </section>
  </Layout>
);

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <NotFound />;

  const related = getRelatedPosts(slug);
  return (
    <Layout>
      <Seo {...getBlogPostSeo(post)} />

      <PageHeader subtitle={post.category} title={post.title} />

      <article className="section-padding bg-white" data-testid="blog-post">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-8 pb-8 border-b border-slate-100">
            <Link
              to="/blog"
              className="flex items-center gap-1.5 text-[#2E4A91] font-medium hover:text-[#2ED573] transition-colors"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" /> Блог
            </Link>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>

          {/* Featured image */}
          <img
            src={post.image}
            alt={post.title}
            className="rounded-2xl shadow-lg w-full mb-10"
          />

          {/* Content */}
          <div>
            {post.content.map((block, i) => (
              <ContentBlock key={i} block={block} />
            ))}
          </div>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="section-padding bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-10 text-center">
              Още от блога
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((rp) => (
                <article
                  key={rp.slug}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden card-hover flex flex-col"
                  data-testid={`related-${rp.slug}`}
                >
                  <Link
                    to={`/blog/${rp.slug}`}
                    className="block overflow-hidden"
                  >
                    <img
                      src={rp.image}
                      alt={rp.title}
                      className="w-full h-44 object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  </Link>
                  <div className="p-6 flex flex-col flex-grow">
                    <span className="text-xs font-semibold text-[#2ED573] uppercase tracking-wider mb-2">
                      {rp.category}
                    </span>
                    <h3 className="text-lg font-bold text-[#2E4A91] mb-3 leading-snug">
                      <Link
                        to={`/blog/${rp.slug}`}
                        className="hover:text-[#2ED573] transition-colors"
                      >
                        {rp.title}
                      </Link>
                    </h3>
                    <Link
                      to={`/blog/${rp.slug}`}
                      className="text-[#2E4A91] font-medium flex items-center gap-2 hover:text-[#2ED573] transition-colors mt-auto"
                    >
                      Прочетете повече <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#2E4A91] to-[#243b75] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Имате нужда от професионално обслужване на клиенти?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Свържете се с нас за безплатна консултация и индивидуална оферта
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/zapazi-chas">
              <Button className="bg-white text-[#2E4A91] hover:bg-slate-100 text-lg px-8 h-14 rounded-lg font-semibold">
                Запазете консултация
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/uslugi">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8 h-14 rounded-lg font-semibold"
              >
                Нашите услуги
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPostPage;
